import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatConversation, ChatMessage, User } from '../../types';
import { api } from '../../services/apiService';
import MessageBubble from './MessageBubble';

interface Props {
  conversation: ChatConversation;
  currentUser: User;
  onMessageSent?: () => void;   // notifier le parent pour refresh liste (last_message)
}

const POLL_INTERVAL_MS = 3000;

const ConversationView: React.FC<Props> = ({ conversation, currentUser, onMessageSent }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Édition : si non-null, message en cours d'édition
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  // Charger les messages — pagination cursor-based (sans cursor = derniers 50)
  const loadMessages = useCallback(async (initial = false) => {
    try {
      const res = await api.chatListMessages(conversation.id);
      // L'API renvoie en DESC, on inverse pour affichage chronologique (vieux → récent)
      const ordered = [...res.messages].reverse();
      setMessages(ordered);
      setError(null);
      // Scroll en bas au chargement initial ou si nouveau message
      const lastId = ordered.length > 0 ? ordered[ordered.length - 1].id : null;
      if (initial || (lastId && lastId !== lastMessageIdRef.current)) {
        lastMessageIdRef.current = lastId;
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      if (initial) setLoading(false);
    }
  }, [conversation.id]);

  // Reset au changement de conversation
  useEffect(() => {
    setMessages([]);
    setLoading(true);
    setInput('');
    lastMessageIdRef.current = null;
    loadMessages(true);
  }, [conversation.id, loadMessages]);

  // Polling 3s tant que la conv est ouverte (pause si onglet caché)
  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (!stopped && !document.hidden) {
        loadMessages(false).catch(() => {});
      }
    };
    const t = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => { stopped = true; window.clearInterval(t); };
  }, [loadMessages]);

  // Envoyer un message
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    if (content.length > 4000) {
      setError('Message trop long (4000 caractères max)');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const sent = await api.chatSendMessage(conversation.id, content);
      // Optimistic update — on ajoute le message en local sans attendre le poll
      setMessages(prev => [...prev, {
        ...sent,
        sender_name: currentUser.name,
        edited_at: null,
        deleted_at: null,
      }]);
      setInput('');
      // Notifier le parent pour rafraîchir la liste (last_message)
      onMessageSent?.();
      // Scroll en bas
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // ----- Édition d'un message -----
  const handleEditRequest = (messageId: string, currentContent: string) => {
    setEditingMessageId(messageId);
    setEditingDraft(currentContent);
    setError(null);
  };

  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditingDraft('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessageId) return;
    const content = editingDraft.trim();
    if (!content) {
      setError('Le message ne peut pas être vide');
      return;
    }
    try {
      await api.chatEditMessage(editingMessageId, content);
      // Optimistic update
      setMessages(prev => prev.map(m =>
        m.id === editingMessageId
          ? { ...m, content, edited_at: new Date().toISOString().slice(0, 19).replace('T', ' ') }
          : m
      ));
      setEditingMessageId(null);
      setEditingDraft('');
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l\'édition');
    }
  };

  // ----- Suppression d'un message (soft delete) -----
  const handleDelete = async (messageId: string) => {
    if (!window.confirm('Supprimer ce message ? Cette action est définitive.')) return;
    try {
      await api.chatDeleteMessage(messageId);
      // Optimistic update : marquer comme supprimé
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, deleted_at: new Date().toISOString().slice(0, 19).replace('T', ' '), content: '' }
          : m
      ));
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la suppression');
    }
  };

  // Titre de la conversation
  const title = useMemo(() => {
    if (conversation.type === 'direct') {
      const other = conversation.participants.find(p => p.user_id !== currentUser.id && p.role === 'member');
      return other?.user_name || 'Conversation';
    }
    return conversation.name || 'Groupe';
  }, [conversation, currentUser.id]);

  const observers = conversation.participants.filter(p => p.role === 'observer');
  const isReadOnly = conversation.my_role === 'observer';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-[rgba(100,140,210,0.2)] px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#e0eaff]">{title}</h3>
            <p className="text-[10px] text-[#7f93bc] mt-0.5">
              {conversation.participants.filter(p => p.role === 'member').length} participant(s)
              {observers.length > 0 && (
                <span className="ml-2" title={observers.map(o => o.user_name).join(', ')}>
                  · 👁️ {observers.length} observateur(s)
                </span>
              )}
              {conversation.is_private && <span className="ml-2">🔒 Privée</span>}
            </p>
          </div>
          {isReadOnly && (
            <span className="text-[10px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1">
              Lecture seule (observateur)
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="text-center text-xs text-[#546a96] py-8">Chargement…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-[#546a96] py-8">Aucun message. Soyez le premier à écrire.</div>
        ) : (
          messages.map((m, i) => {
            const isMine = m.sender_id === currentUser.id;
            // Afficher le nom uniquement quand l'expéditeur change ou en début
            const prev = i > 0 ? messages[i - 1] : null;
            const showSender = !prev || prev.sender_id !== m.sender_id;
            const isAdmin = currentUser.role === 'COMPANY_ADMIN' || currentUser.role === 'SUPER_ADMIN';
            return (
              <MessageBubble
                key={m.id}
                message={m}
                isMine={isMine}
                isAdmin={isAdmin}
                showSender={showSender && conversation.type !== 'direct'}
                onEdit={handleEditRequest}
                onDelete={handleDelete}
              />
            );
          })
        )}
      </div>

      {/* Bannière d'édition (overlay au-dessus de l'input) */}
      {editingMessageId && (
        <form onSubmit={handleEditSubmit} className="border-t border-amber-500/40 bg-amber-500/10 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-amber-300 uppercase tracking-widest">✏️ Édition du message</span>
            <button type="button" onClick={handleEditCancel}
              className="text-[10px] font-bold text-[#7f93bc] hover:text-[#e0eaff]">Annuler</button>
          </div>
          {error && (
            <div className="mb-2 p-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              value={editingDraft}
              onChange={e => setEditingDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEditSubmit(e);
                } else if (e.key === 'Escape') {
                  handleEditCancel();
                }
              }}
              autoFocus
              aria-label="Modifier le message"
              placeholder="Modifier le contenu du message…"
              className="orion-settings-input flex-1 rounded-xl px-3 py-2 text-sm resize-none"
              rows={2}
              maxLength={4000}
            />
            <button type="submit" disabled={!editingDraft.trim()}
              className="orion-settings-primary-btn text-white font-bold px-4 rounded-xl text-sm disabled:opacity-50 self-stretch">
              Enregistrer
            </button>
          </div>
        </form>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="border-t border-[rgba(100,140,210,0.2)] px-4 py-3 flex-shrink-0">
        {error && !editingMessageId && (
          <div className="mb-2 p-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder={isReadOnly ? 'Lecture seule — vous ne pouvez pas écrire' : 'Écrire un message… (Entrée = envoyer, Maj+Entrée = saut de ligne)'}
            disabled={isReadOnly || sending}
            className="orion-settings-input flex-1 rounded-xl px-3 py-2 text-sm resize-none disabled:opacity-50"
            rows={2}
            maxLength={4000}
          />
          <button
            type="submit"
            disabled={isReadOnly || sending || !input.trim()}
            className="orion-settings-primary-btn text-white font-bold px-4 rounded-xl text-sm disabled:opacity-50 self-stretch"
          >
            {sending ? '…' : 'Envoyer'}
          </button>
        </div>
        <div className="text-[9px] text-[#546a96] mt-1 text-right">{input.length} / 4000</div>
      </form>
    </div>
  );
};

export default ConversationView;
